import crypto from 'crypto'
import uuid from 'uuid-random'
import mitt from 'mitt'

import authConfig from '@/config/authConfig'
import { AUTH_STYLE_POPUP, AUTH_STYLE_REDIRECT, AUTH_STYLE_SSO } from '@/config/authConfig'
import AuthzService from '@/services/authzService.js'
import { AVATAR_BASE_URL } from '@/config/appConfig.js'
import { createAuth0Agent } from '@/services/agents/auth0/auth0Agent.js'
import { createMsalAgent } from '@/services/agents/msal/msalAgent.js'
import { i18n } from '@/plugins/localePlugin.js'
import { analyticsService } from '@/services/analyticsService.js'

const EVENT_NAME = '$auth:ready'

// user is defintely not authorized or an error prevented verification
class AuthnService {
  constructor() {
    // initialize agent
    this.agent = null
    this.ready = false
    this.error = null
    this.ok = false
    this.timer = null

    // initialize services
    this.authzService = new AuthzService()
    this.$bus = mitt()
    this.$t = (msg, ...args) => i18n.t(msg, ...args)

    // setup authentication state
    this.authStyle = authConfig.authStyle
    this.user = null

    // tokens (all are JWT-based)
    this.accessToken = null // from IdP (to call /userinfo endpoint)
    this.idToken = null // from IdP
    this.idTokenClaims = {}
  }

  /*
   * Service Management Methods
   */

  async initialize(tenant) {
    console.debug('[authnService]: Initialization begins...')

    this.ready = false
    this.ok = false

    try {
      // initialize the authorization service
      const authorizedDomains = [tenant.domain, ...tenant.authorizedDomains]
      console.debug('[authService]: user domain=', tenant.domain)
      console.debug('[authService]: authorized domains=', authorizedDomains)
      await this.authzService.initialize({ authorizedDomains })

      // initialize the authentication service
      this.agent =
        authConfig.authAgent === 'msal'
          ? await createMsalAgent(tenant.auth)
          : await createAuth0Agent(tenant.auth)

      this.ok = true
      return true
    } catch (e) {
      console.error('[authnService]: Unable to initialize.', e)
      this.error = e.message
      this.ok = false
      throw e
    } finally {
      this.ready = true
      this.$bus.emit(EVENT_NAME, { status: this.ok })
      console.debug('[authnService]: Initialization complete! ok=', this.ok)
    }
  }

  isReady() {
    return this.ready
  }

  isOK() {
    return this.ok
  }

  getError() {
    return this.error
  }

  async waitUntilReady() {
    return new Promise((resolve, reject) => {
      // check before using a timer
      if (this.ready) {
        this.ok ? resolve(true) : reject(new Error(this.error))
      }

      // start a timer (which is cancelled when $auth is ready)
      const timeout = 15000 // 15 seconds
      this.timer = setTimeout(() => {
        // if the timeout occurs, then $auth took too long to initialize
        const errMsg = '[authnService]: Timed out waiting for $auth to be ready!'
        // remove the event bus listener (stop waiting for $auth to be ready)
        this.$bus.off(EVENT_NAME)
        // final check before timeout
        if (this.ready) {
          // check whether initialization succeeded
          this.ok ? resolve(true) : reject(new Error(this.error))
        } else {
          // reject the promise (because $auth took too long)
          reject(new Error(errMsg))
        }
      }, timeout)

      // declare the event bus listener (that runs if the EVENT_NAME event is received)
      const onAuthReady = (_event) => {
        // clear the timer
        clearTimeout(this.timer)
        // remove the event bus listener
        this.$bus.off(EVENT_NAME)
        // resolve the promise (because $auth is ready!)
        this.ok ? resolve(true) : reject(new Error(this.error))
      }

      // wait for $auth to be ready (and clear the timer/bus when it is)
      this.ready ? onAuthReady() : this.$bus.on(EVENT_NAME, onAuthReady)
    })
  }

  cancel(release) {
    // cancel timeout timer (so no timeout error occurs)
    if (this.timer) {
      clearTimeout(this.timer)
    }

    // optionally, artificially wakeup the service
    if (release) {
      this.isReady = true
      this.ok = false
      this.error = '[authnService]: Cancelled.'

      this.$bus.emit(EVENT_NAME, this.ok)
    }
  }

  /*
   * Response Handling Methods
   */

  async handleRedirect() {
    const response = await this.agent.handleRedirect()

    if (response) {
      if (authService.isAuthenticated()) {
        analyticsService.identify(this.getUserAccount())
        analyticsService.capture('user_login', { ...this.getEventProperties() })
      }
    }

    return response
  }

  async restoreSession() {
    await this.agent.restoreSession()
  }

  /*
   * SignIn / SignOut Methods
   */

  async signIn(options = {}) {
    // FIXME: need to handle state & redirectURI for non-redirect logins
    let retry = true
    while (retry) {
      if (this.isSSOLogin()) {
        options.prompt = 'none'
        retry = await this.agent.loginWithSSO(options)
      } else if (this.isPopupLogin()) {
        options.prompt = 'select_account'
        retry = await this.agent.loginWithPopup(options)
      } else {
        options.prompt = 'select_account'
        retry = await this.agent.loginWithRedirect(options)
      }
    }
  }

  async signOut(options = {}) {
    analyticsService.capture('user_logout', { ...this.getEventProperties() })
    analyticsService.reset()
    this.authzService.clearAuthorization()
    await this.agent.logout(options)
  }

  isAuthenticated() {
    // this.agent is null if timeout occurs while initializing
    return this.agent?.isAuthenticated()
  }

  async authenticate({ redirectURI, loginOptions }) {
    // ensure $auth is initialized (see authPlugin)
    console.log('[authnService]: waiting for agent to be be ready...')
    await this.waitUntilReady()

    // authenticate if necessary
    console.debug('[authnService]: Checking authentication...', this.isAuthenticated())
    if (!this.isAuthenticated()) {
      const options = {
        // returned on redirect from identity proviider
        state: {
          redirectURI: redirectURI || '/'
          // locale: locale
        },
        // sent to identity provider
        prompt: loginOptions?.prompt || 'none', // likely overriden by login methods
        loginHint: loginOptions?.login_hint || '',
        domainHint: loginOptions?.domain_hint || '',
        locale: loginOptions.locale,
        extraQueryParams: loginOptions?.eqp || {} // passed to IdP
      }

      // note: login may result in a redirect, hence processing ends here
      await this.signIn(options)

      if (authService.isAuthenticated()) {
        analyticsService.identify(this.getUserAccount())
        analyticsService.capture('user_login', { ...this.getEventProperties() })
      }
    }

    return this.getUserId()
  }

  /*
   * Login/Logout Methods
   */

  isRedirectLogin() {
    return this.authStyle === AUTH_STYLE_REDIRECT
  }

  isPopupLogin() {
    return this.authStyle === AUTH_STYLE_POPUP
  }

  isSSOLogin() {
    return this.authStyle === AUTH_STYLE_SSO
  }

  /*
   * User Methods
   */

  getUserId() {
    return this.agent.getUserId()
  }

  getUserEmail() {
    // preserve case as entered by user
    return this.agent.getUserEmail()
  }

  getUserDisplayName() {
    return this.agent.getUserDisplayName() || this.$t('account.guest')
  }

  getUserPicture() {
    return this.agent.getUserPicture()
  }

  getUserAvatar() {
    const email = this.getUserEmail()?.toLowerCase() || ''
    const hash = crypto.createHash('md5').update(email).digest('hex')
    return `${AVATAR_BASE_URL}/${hash}?d=mp`
  }

  getUserUniqueId() {
    return this.agent.getUserUniqueId() || uuid()
  }

  getUserAccount() {
    return {
      id: this.getUserUniqueId(),
      userId: this.getUserId(),
      subject: this.getUserSubject(),
      name: this.getUserDisplayName(),
      avatar: this.getUserAvatar(),
      email: this.getUserEmail()
    }
  }

  getEventProperties() {
    return {
      user_id: this.getUserId(),
      user_name: this.getUserDisplayName(),
      user_email: this.getUserEmail(),
      user_subject: this.getUserSubject()
    }
  }

  getUserSubject() {
    return this.agent.getUserSubject()
  }

  /*
   * Token Methods
   */

  async getIdTokenClaims() {
    return this.agent.getIdTokenClaims()
  }

  async acquireTokens(options) {
    return this.agent.acquireTokens(options)
  }

  /*
   * Authorization Methods
   */

  async authorize(userId) {
    await this.authzService.authorize(userId)
    return
  }

  clearAuthorization() {
    this.authzService.clearAuthorization()
  }

  isAuthorized() {
    return this.authzService.isUserAuthorized(this.getUserId())
  }
}

export default AuthnService
export const authService = new AuthnService()
